import { Controller } from "@hotwired/stimulus"

// Energy in kcal or kilojoules.
//
// NZ and AU panels lead with kilojoules, usually in larger type than the
// calories beside them — so a kJ figure lands in a calorie field and reads
// as four times the real energy, looking perfectly normal.
//
// The unit is a control inside the field rather than something you type.
// kcal is always what gets stored: the visible field is a display, the
// hidden one is what the form posts.
const KJ_PER_KCAL = 4.184

export default class extends Controller {
  static targets = ["input", "store", "unit"]

  // Which unit the field STARTS in, from the user's country — NZ and AU
  // labels lead with kilojoules, and defaulting those users to kcal is what
  // produced the 4.184× errors this control exists to prevent.
  //
  // A value rather than a hardcoded default so the server decides: the
  // country lives on the user, and the browser has no business guessing at
  // it a second time.
  static values = { unit: { type: String, default: "kcal" } }

  connect() {
    this.unit = this.unitValue === "kJ" ? "kJ" : "kcal"
    this.unitTarget.textContent = this.unit

    // The field renders a STORED value, which is always kcal. Starting in kJ
    // therefore means converting what is already there — otherwise editing
    // an existing food would show its kcal figure with "kJ" beside it, which
    // is the same lie in the other direction.
    const stored = parseFloat(this.inputTarget.value)
    if (this.unit === "kJ" && !isNaN(stored)) {
      this.inputTarget.value = Math.round(stored * KJ_PER_KCAL)
    }

    this.convert()
  }

  toggle() {
    // Convert what is already typed rather than clearing it — changing the
    // unit should reinterpret the number, not throw it away.
    const shown = parseFloat(this.inputTarget.value)
    this.unit = this.unit === "kcal" ? "kJ" : "kcal"

    if (!isNaN(shown)) {
      this.inputTarget.value = this.unit === "kJ"
        ? Math.round(shown * KJ_PER_KCAL)
        : Math.round((shown / KJ_PER_KCAL) * 10) / 10
    }

    this.unitTarget.textContent = this.unit
    this.convert()
  }

  convert() {
    const shown = parseFloat(this.inputTarget.value)

    if (isNaN(shown)) {
      this.storeTarget.value = ""
      // The VISIBLE field carries `required`, because the hidden one cannot
      // be reported on — a browser refuses to focus a hidden control and
      // silently declines the whole submit. Clearing it here keeps the two
      // honest: what is on screen is what will be posted.
      return
    }

    // kcal is always what gets stored, whichever unit is on show.
    const kcal = this.unit === "kJ" ? shown / KJ_PER_KCAL : shown
    this.storeTarget.value = Math.round(kcal * 10) / 10
  }
}
